// ─────────────────────────────────────────────────────────────────────────────
// ProximityRadarCanvas — WHO WAS LAST ACTIVE, AND WHEN, for the Console right panel.
//
// A live radar read STRAIGHT OFF THE ACTIVITY INDEX. The radar is a SECOND CONSUMER
// of GET /api/rexy/activity, not a link in her chain (activity → notes → reports →
// dreamstate): it reads who/when, plots by recency, and writes nothing. No harness
// touch, no artifact, no bleed by construction.
//
// Fixed 188×188 canvas. Three recency bands matching ACTIVITY's own rolling 12h
// window — inner < 2h, middle 2–6h, outer 6–12h — with the NEWEST activity nearest
// the centre (radius reflects actual age within its band, not a fixed midpoint).
// Two subtle inner rings + one bright outer boundary; a sweep line rotates
// continuously and FLASHES each dot's label as it passes.
//
// IDENTITY IS INHERITED: `who` is plotted verbatim (details.agent || details.domain
// on the board), so ARTHUR/MACK/TRUFFLE and CODE/SECURITY appear exactly as they do
// on ACTIVITY — no mapping table, no invented identities.
//
// HONEST STATES: a successful-but-empty window says NO ACTIVE AGENTS; a failed auth
// / read says ACTIVITY UNAVAILABLE. The radar never claims inactivity it couldn't
// verify, and never renders a fictional dot.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../lib/rexyConsole';
import { ringForAge, colourForDomain, RadarAgent } from '../lib/radarAgents';
import type { ActivityIndexResponse } from './dashboard/activityFixtures';
import { ACTIVITY, authHeaders } from '../data/dashboard/endpoints';

const SIZE = 188; // fixed canvas px (spec)
const REF = 160; // reference size the proportions were tuned at
const SCALE = SIZE / REF;
const SWEEP_SPEED = 0.6; // rad/s (~10.5s per rotation)
const TRAIL = 0.55; // wedge width behind the leading edge (rad)
const FLASH_WINDOW = 0.15; // ±rad around a dot where the sweep "hits" it
const POLL_MS = 30_000; // matches ACTIVITY's own 30s cadence

// Three rings: two subtle inner (bands 1–2) + one bright outer boundary (band 3).
const RING_FRAC = [0.36, 0.63, 0.9];
// Radial band each ring's dots occupy, as a fraction of C. A dot's exact radius
// within its band is interpolated by AGE (newest → inner edge), never the midpoint.
const BAND_FRAC: [number, number][] = [
  [0.12, 0.36],
  [0.4, 0.63],
  [0.67, 0.9],
];
// Age bounds (ms) per band — aligned with radarAgents.ringForAge. Drives the
// within-band radius so the newest activity sits nearest the centre.
const H = 60 * 60 * 1000;
const BAND_AGE_MS: [number, number][] = [
  [0, 2 * H],
  [2 * H, 6 * H],
  [6 * H, 12 * H],
];
const RING_DOT = [7, 5, 3.5];
const RING_ALPHA = [1.0, 0.68, 0.4];
const LABEL_FONT = Math.min(13, Math.max(8, Math.round(7 * SCALE)));

interface Placed {
  agent: RadarAgent;
  ring: number;
  x: number;
  y: number;
  dot: number;
  alpha: number;
  angle: number;
}

// Smallest absolute angular distance between two angles (rad).
function angDist(a: number, b: number): number {
  let d = Math.abs(a - b) % (Math.PI * 2);
  if (d > Math.PI) d = Math.PI * 2 - d;
  return d;
}

// Stable angle for an identity derived from its name — djb2 hash mod 2π.
// Same name → same bearing across every re-render and poll tick.
function stableAngle(name: string): number {
  let h = 5381;
  for (let i = 0; i < name.length; i++) {
    h = ((h << 5) + h) ^ name.charCodeAt(i);
    h = h >>> 0; // keep uint32
  }
  return (h / 0xffffffff) * Math.PI * 2;
}

type FeedStatus = 'loading' | 'ok' | 'error';

export default function ProximityRadarCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // The ACTIVITY index — the SAME source the board renders. Groups drive the dots;
  // status distinguishes a genuinely-empty window from a failed read (honest states).
  const [groups, setGroups] = useState<ActivityIndexResponse['groups']>([]);
  const [status, setStatus] = useState<FeedStatus>('loading');

  useEffect(() => {
    let alive = true;
    const fetchActivity = () => {
      fetch(ACTIVITY, { headers: authHeaders() })
        .then(async (response) => {
          const body = (await response.json()) as Partial<ActivityIndexResponse>;
          if (!response.ok || body.success !== true || !Array.isArray(body.groups)) {
            throw new Error('activity unavailable');
          }
          return body as ActivityIndexResponse;
        })
        .then((index) => {
          if (!alive) return;
          setGroups(index.groups);
          setStatus('ok');
        })
        .catch(() => {
          if (!alive) return;
          setGroups([]);
          setStatus('error'); // auth / network / parse / payload failure
        });
    };
    fetchActivity();
    const poll = setInterval(fetchActivity, POLL_MS);
    return () => {
      alive = false;
      clearInterval(poll);
    };
  }, []);

  // Derive most-recent-per-identity from the index: flatten groups[].rows, key by
  // domain + who, keep the newest valid ts per key, plot by age. Only ts/who/domain
  // are read — no detail call, no content-bearing field.
  const placed = useMemo<Placed[]>(() => {
    const now = Date.now();
    const C = SIZE / 2;

    // Newest per (domain|who). A plain object accumulator + Object.values keeps this
    // free of the ES2015 iterator protocol, so it compiles under the es5 target the
    // dev server enforces (no --downlevelIteration dependency).
    const newest: Record<string, { who: string; domain: string; ts: number }> = {};
    groups.forEach((g) => {
      (g.rows || []).forEach((row) => {
        const t = Date.parse(row.ts);
        if (Number.isNaN(t)) return; // invalid ts → skip, never plot a guess
        const key = `${row.domain.toUpperCase()}|${row.who.toUpperCase()}`;
        const prev = newest[key];
        if (!prev || t > prev.ts) newest[key] = { who: row.who, domain: row.domain, ts: t };
      });
    });

    return Object.values(newest)
      .map((e) => {
        const ring = ringForAge(now - e.ts);
        if (ring === 0) return null;
        const age = Math.max(0, now - e.ts);
        const [bi, bo] = BAND_FRAC[ring - 1];
        const [aLo, aHi] = BAND_AGE_MS[ring - 1];
        // Interpolate radius by ACTUAL AGE within the band: newest → inner edge
        // (nearest centre), oldest-in-band → outer edge. No midpoint clustering.
        const f = aHi > aLo ? Math.min(1, Math.max(0, (age - aLo) / (aHi - aLo))) : 0;
        const r = (bi + f * (bo - bi)) * C;
        const angle = stableAngle(`${e.domain}|${e.who}`);
        const agent: RadarAgent = {
          id: `${e.domain}-${e.who}`,
          name: e.who,
          domain: e.domain,
          lastActive: e.ts,
          colour: colourForDomain(e.domain),
          angle,
        };
        return {
          agent,
          ring,
          x: C + r * Math.cos(angle),
          y: C + r * Math.sin(angle),
          dot: RING_DOT[ring - 1] * SCALE * 0.5,
          alpha: RING_ALPHA[ring - 1],
          angle,
        } as Placed;
      })
      .filter((p): p is Placed => p !== null)
      .sort((a, b) => b.ring - a.ring);
  }, [groups]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Crisp on HiDPI: back the 188px CSS box with a dpr-scaled buffer.
    const dpr = window.devicePixelRatio || 1;
    canvas.width = SIZE * dpr;
    canvas.height = SIZE * dpr;
    canvas.style.width = `${SIZE}px`;
    canvas.style.height = `${SIZE}px`;
    ctx.scale(dpr, dpr);

    const C = SIZE / 2;
    const Rmax = RING_FRAC[2] * C;
    // Per-agent flash opacity, persisted across frames.
    const flash = new Array<number>(placed.length).fill(0);
    let raf = 0;

    const draw = () => {
      const t = performance.now() / 1000;
      const lead = (t * SWEEP_SPEED) % (Math.PI * 2);
      ctx.clearRect(0, 0, SIZE, SIZE);

      // Inner rings 1–2: subtle cyan.
      ctx.lineWidth = 1 * SCALE;
      ctx.strokeStyle = 'rgba(0,191,255,0.12)';
      for (let i = 0; i < 2; i++) {
        ctx.beginPath();
        ctx.arc(C, C, RING_FRAC[i] * C, 0, Math.PI * 2);
        ctx.stroke();
      }

      // Outer boundary ring: neon glow then crisp bright stroke.
      ctx.save();
      ctx.shadowColor = 'rgba(0,191,255,0.9)';
      ctx.shadowBlur = 8 * SCALE;
      ctx.lineWidth = 2.5 * SCALE;
      ctx.strokeStyle = 'rgba(0,191,255,0.55)';
      ctx.beginPath();
      ctx.arc(C, C, Rmax, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
      ctx.lineWidth = 1.5 * SCALE;
      ctx.strokeStyle = 'rgba(0,191,255,0.9)';
      ctx.beginPath();
      ctx.arc(C, C, Rmax, 0, Math.PI * 2);
      ctx.stroke();

      // Sweep wedge (faint trail behind the leading edge).
      ctx.beginPath();
      ctx.moveTo(C, C);
      ctx.arc(C, C, Rmax, lead - TRAIL, lead);
      ctx.closePath();
      ctx.fillStyle = 'rgba(0,191,255,0.10)';
      ctx.fill();

      // Sweep line (leading edge).
      ctx.lineWidth = 1.5 * SCALE;
      ctx.strokeStyle = 'rgba(0,191,255,0.65)';
      ctx.beginPath();
      ctx.moveTo(C, C);
      ctx.lineTo(C + Rmax * Math.cos(lead), C + Rmax * Math.sin(lead));
      ctx.stroke();

      // Agent dots.
      for (let i = 0; i < placed.length; i++) {
        const p = placed[i];
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.agent.colour;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.dot, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      // Centre — Rexy/user, fixed white dot on top.
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(C, C, 3 * SCALE, 0, Math.PI * 2);
      ctx.fill();

      // Honest states — only when there are no dots. A successful-but-empty window
      // says NO ACTIVE AGENTS; a failed read says ACTIVITY UNAVAILABLE. We never
      // claim inactivity we couldn't verify, and never invent a dot.
      if (placed.length === 0) {
        ctx.font = `${LABEL_FONT}px ${REXY_FONT}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const label =
          status === 'error'
            ? 'ACTIVITY UNAVAILABLE'
            : status === 'loading'
            ? ''
            : 'NO ACTIVE AGENTS';
        if (label) {
          ctx.fillStyle =
            status === 'error' ? 'rgba(239,68,68,0.55)' : 'rgba(0,191,255,0.30)';
          ctx.fillText(label, C, C + RING_FRAC[1] * C * 0.62);
        }
      }

      // Flash labels — fast in inside the sweep window, slow out after. Side-flip so
      // right-half dots label right, left-half dots label left.
      ctx.font = `${LABEL_FONT}px ${REXY_FONT}`;
      ctx.textBaseline = 'middle';
      for (let i = 0; i < placed.length; i++) {
        const p = placed[i];
        const inWindow = angDist(lead, p.angle) < FLASH_WINDOW;
        const target = inWindow ? 1 : 0;
        const rate = inWindow ? 0.18 : 0.05;
        flash[i] += (target - flash[i]) * rate;
        if (flash[i] < 0.01) continue;
        const onRight = p.x > C;
        ctx.globalAlpha = flash[i];
        ctx.fillStyle = p.agent.colour;
        ctx.textAlign = onRight ? 'left' : 'right';
        ctx.fillText(p.agent.name, onRight ? p.x + p.dot + 3 : p.x - p.dot - 3, p.y);
      }
      ctx.globalAlpha = 1;

      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [placed, status]);

  return (
    <div style={styles.card}>
      <div style={styles.sectionHeader}>║ PROXIMITY</div>
      <div style={styles.radarWrap}>
        <canvas ref={canvasRef} width={SIZE} height={SIZE} />
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 14,
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
  },
  sectionHeader: {
    fontWeight: 700,
    fontFamily: REXY_FONT,
    fontSize: 12,
    letterSpacing: 2,
    color: REXY_CONSOLE.primary,
    marginBottom: 10,
  },
  radarWrap: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};
